import { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, X, Search } from 'lucide-react';

export interface Option {
  id: string;
  label: string;
  disabled?: boolean;
  meta?: any; // Informações adicionais para exibição
}

interface MultiSelectCheckboxProps {
  options: Option[];
  selectedValues: string[];
  onChange: (selectedIds: string[]) => void;
  onSelectAll?: (selected: boolean) => void;
  placeholder?: string;
  label?: string;
  error?: boolean;
  hint?: string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  maxHeight?: number;
  disabled?: boolean;
  className?: string;
  theme?: 'light' | 'dark';
}

export default function MultiSelectCheckbox({
  options,
  selectedValues,
  onChange,
  onSelectAll,
  placeholder = 'Selecione as opções',
  label,
  error,
  hint,
  showSelectAll = true,
  selectAllLabel = 'Selecionar todas as opções',
  searchable = true,
  searchPlaceholder = 'Pesquisar',
  maxHeight = 250,
  disabled = false,
  className = '',
  theme,
}: MultiSelectCheckboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filtrar opções com base no termo de pesquisa
  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Verificar se todas as opções disponíveis estão selecionadas
  const allSelected = options.length > 0 && options.every(option => 
    selectedValues.includes(option.id) || option.disabled
  );

  // Verificar se a opção "all" está selecionada
  const allOptionSelected = selectedValues.includes('all');

  // Texto para exibir no seletor
  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (allOptionSelected) return options.find(opt => opt.id === 'all')?.label || selectAllLabel;
    if (allSelected) return selectAllLabel;
    if (selectedValues.length === 1) {
      const selected = options.find(opt => opt.id === selectedValues[0]);
      return selected ? selected.label : placeholder;
    }
    return `${selectedValues.length} opções selecionadas`;
  };

  // Manipular clique fora do dropdown para fechá-lo
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focar no campo de pesquisa quando o dropdown é aberto
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Manipular seleção/desseleção de uma opção
  const handleOptionChange = (optionId: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedValues, optionId]);
    } else {
      onChange(selectedValues.filter(id => id !== optionId));
    }
  };

  // Manipular seleção/desseleção de todas as opções
  const handleSelectAll = (checked: boolean) => {
    if (onSelectAll) {
      onSelectAll(checked);
      return;
    }
    
    if (checked) {
      const allOptionIds = options
        .filter(option => !option.disabled)
        .map(option => option.id);
      onChange(allOptionIds);
    } else {
      onChange([]);
    }
  };

  // Limpar todas as seleções
  const handleClearAll = () => {
    onChange([]);
    setIsOpen(false);
  };

  // ==============================
  // Visual alignment with Select
  // ==============================
  const hasSelection = selectedValues.length > 0;
  let selectClasses = "h-11 w-full rounded-lg border px-4 py-2.5 pr-11 text-sm shadow-theme-xs cursor-pointer flex items-center justify-between focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 ";
  let labelColor = hasSelection ? "text-gray-800 dark:text-white/90" : "text-gray-400 dark:text-gray-400";
  let chevronColor = "text-gray-500 dark:text-gray-400";
  let dropdownClasses = "absolute z-10 w-full rounded-lg shadow-lg ";

  if (theme === 'light') {
    selectClasses += 'border-gray-200 bg-white ';
    labelColor = hasSelection ? 'text-gray-800' : 'text-gray-400';
    chevronColor = 'text-gray-500';
    dropdownClasses += 'bg-white border border-gray-300 ';
  } else if (theme === 'dark') {
    selectClasses += 'border-gray-700 bg-gray-900 ';
    labelColor = hasSelection ? 'text-white/90' : 'text-gray-400';
    chevronColor = 'text-gray-400';
    dropdownClasses += 'bg-gray-900 border border-gray-700 ';
  } else {
    // default (matches Select default path)
    selectClasses += 'border-gray-200 bg-transparent dark:border-gray-800 dark:bg-gray-900 ';
    labelColor = hasSelection ? 'text-gray-800 dark:text-white/90' : 'text-gray-400 dark:text-gray-400';
    chevronColor = 'text-gray-500 dark:text-gray-400';
    dropdownClasses += 'bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 ';
  }

  if (error) {
    selectClasses += ' border-red-500 dark:border-red-500 ';
  }
  if (disabled) {
    selectClasses += ' cursor-not-allowed opacity-75 ';
  }

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      {label && (
        <label className="mb-2.5 block text-black dark:text-white">
          {label}
        </label>
      )}
      
      <div 
        className={selectClasses}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className={`flex-grow truncate ${labelColor}`}>
          {getDisplayText()}
        </div>
        <div className={`flex items-center gap-2 ${chevronColor}`}>
          {selectedValues.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              disabled={disabled}
            >
              <X size={16} />
            </button>
          )}
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      {error && hint && (
        <p className="mt-1 text-xs text-red-500">{hint}</p>
      )}

      {isOpen && (
        <div className={`${dropdownClasses} mt-1`}>
          <div className="p-2 flex flex-col">
            {searchable && (
              <div className="relative mb-2">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="w-full rounded border border-gray-200 py-2 pl-10 pr-4 text-sm outline-none focus:border-primary dark:border-gray-800 dark:bg-gray-900 dark:text-white/90"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            )}

            {showSelectAll && options.length > 0 && (
              <div className="px-2 py-1.5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="select-all-options"
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="select-all-options" className="ml-2 block text-sm font-medium text-gray-700 dark:text-white/90">
                    {selectAllLabel}
                  </label>
                </div>
              </div>
            )}

            <div 
              className="overflow-y-auto"
              style={{ maxHeight: `${maxHeight}px` }}
            >
              {filteredOptions.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhuma opção encontrada
                </div>
              ) : (
                filteredOptions.map(option => (
                  <div key={option.id} className="px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`option-${option.id}`}
                        checked={option.id === 'all' ? allOptionSelected : (allOptionSelected || selectedValues.includes(option.id))}
                        onChange={(e) => handleOptionChange(option.id, e.target.checked)}
                        disabled={option.disabled || (option.id !== 'all' && allOptionSelected)}
                        className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label 
                        htmlFor={`option-${option.id}`} 
                        className={`ml-2 block text-sm ${option.disabled ? 'text-gray-400' : 'text-gray-700 dark:text-white/90'}`}
                      >
                        {option.label}
                      </label>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
